import { MenuItem, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  PossibleQuestionModelType,
  Question,
  Sector,
} from '@/domain/impact/impact.entity';
import { selectImpactEssQuestionsMap } from '@/domain/impact/impact.selectors.ts';
import { EssTopicValues } from '@/domain/impact-calculate/entities/calculate.entity';
import { submitImpactEssResponse } from '@/domain/impact-ess/actions/submit-impact-ess-response.action';
import { EssQuestionValue } from '@/domain/impact-ess/entities/question.entity';
import {
  EssSection,
  EssTopic,
} from '@/domain/impact-ess/entities/question.entity';
import {
  selectEssDebugMode,
  selectImpactEssAnswersMap,
} from '@/domain/impact-ess/impact-ess.selectors';
import { ModelVersionESS } from '@/domain/project/entities/project.entity';
import { selectCurrentProject } from '@/domain/project/project.selectors';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { QuizLayout } from '@/ui/pages/app/components/quiz-panel/QuizLayout';
import { getEssSectionName } from '@/ui/utils/ess-env-names-and-format.ts';

import { QuizPanelHideCompletedCheckBox } from '../QuizPanelHideCompletedCheckBox';
import { prepareEssTopicsDisplayed } from './prepare-ess-topics-displayed';
import { questionsBySectorEss } from './questions-by-sector-ess';

type Inputs = { [key: string]: string };

interface IProps {
  topic?: EssTopic;
  sector?: Sector;
  essQuizLayout: Record<EssTopic, { sections: Array<EssSection> }>;
  displayTopicPicker?: boolean;
  dateId?: number;
  modelESS?: ModelVersionESS;
  useSectionTitleSeparators?: boolean;
  displayValidationButtons?: boolean;
}

export function ESSQuiz({
  topic,
  sector,
  essQuizLayout,
  displayTopicPicker = true,
  dateId,
  modelESS,
  useSectionTitleSeparators,
  displayValidationButtons,
}: IProps) {
  const dispatch = useAppDispatch();

  const [selectedTopic, setSelectedTopic] = useState<EssTopic>(
    EssTopicValues[0],
  );

  useEffect(() => {
    if (topic && EssTopicValues.includes(topic)) {
      setSelectedTopic(topic);
    }
  }, [topic]);

  const project = useAppSelector(selectCurrentProject);
  const questions: Map<
    Question<PossibleQuestionModelType.ESS>['paramId'],
    Question<PossibleQuestionModelType.ESS>
  > = useAppSelector(selectImpactEssQuestionsMap);
  const responses: Map<string, EssQuestionValue> = useAppSelector(
    selectImpactEssAnswersMap,
  );

  const essTopicsDisplayed = prepareEssTopicsDisplayed(essQuizLayout);

  const onSubmitResponse = async (response: EssQuestionValue) => {
    dispatch(
      submitImpactEssResponse({
        projectId: dateId || project?.id || -1,
        response,
        essModelVersion: modelESS,
      }),
    );
  };

  return (
    <Stack direction={'column'} justifyContent={'start'}>
      <QuizPanelHideCompletedCheckBox />
      {displayTopicPicker && (
        <TopicPicker
          topics={essTopicsDisplayed}
          selectedTopic={selectedTopic}
          setSelectedTopic={setSelectedTopic}
        />
      )}
      <ESSForm
        questions={questions}
        responses={responses}
        selectedTopic={selectedTopic}
        sector={sector}
        essQuizLayout={essQuizLayout}
        useSectionTitleSeparators={useSectionTitleSeparators}
        displayValidationButtons={displayValidationButtons}
        onSubmitResponse={onSubmitResponse}
      />
    </Stack>
  );
}

const TopicPicker = ({
  topics,
  selectedTopic,
  setSelectedTopic,
}: {
  topics: EssTopic[];
  selectedTopic: EssTopic;
  setSelectedTopic: (topic: EssTopic) => void;
}) => {
  return (
    <Stack sx={{ my: 2 }}>
      <TextField
        select
        onChange={(e) => setSelectedTopic(e.target.value as EssTopic)}
        value={selectedTopic}
        sx={{ color: 'white' }}
      >
        {topics.map((topic) => (
          <MenuItem key={topic} value={topic}>
            {translate(`Ess.Topics.${topic as EssTopic}`)}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};

const ESSForm = ({
  questions,
  responses,
  selectedTopic,
  sector,
  essQuizLayout,
  useSectionTitleSeparators,
  displayValidationButtons,
  onSubmitResponse,
}: {
  questions: Map<
    Question<PossibleQuestionModelType.ESS>['paramId'],
    Question<PossibleQuestionModelType.ESS>
  >;
  responses: Map<string, EssQuestionValue>;
  selectedTopic: EssTopic;
  sector?: Sector;
  essQuizLayout: Record<EssTopic, { sections: Array<EssSection> }>;
  useSectionTitleSeparators?: boolean;
  displayValidationButtons?: boolean;
  onSubmitResponse: (response: EssQuestionValue) => void;
}) => {
  const { handleSubmit, register, formState, control } = useForm<Inputs>({
    defaultValues: defineValuesFromExistingResponses(responses),
    mode: 'onChange',
  });

  const { errors } = formState;

  const onSubmitInput = (data: Inputs, essKey: string) => {
    const response: EssQuestionValue = {
      id: -1,
      paramId: essKey,
      value: data[essKey],
    };
    onSubmitResponse(response);
  };

  const debugMode = useAppSelector(selectEssDebugMode);

  return (
    <form
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <QuizLayout<PossibleQuestionModelType.ESS>
        questions={questions}
        selectedTopic={selectedTopic}
        register={register}
        errors={errors}
        control={control}
        handleSubmit={handleSubmit}
        sector={sector}
        sections={essQuizLayout[selectedTopic].sections}
        questionsBySector={questionsBySectorEss}
        sectionNameFunction={getEssSectionName}
        useSectionTitleSeparators={useSectionTitleSeparators}
        displayValidationButtons={displayValidationButtons}
        debugMode={debugMode}
        onSubmitInput={onSubmitInput}
      />
    </form>
  );
};

function defineValuesFromExistingResponses(
  responses: Map<string, EssQuestionValue>,
) {
  const values: Record<string, string> = {};
  for (const response of responses.values()) {
    values[response.paramId] = response.value;
  }
  return values;
}
